import type { AppDispatch, RootState } from '../../store';
import { addElements, removeElements, updateTrackNodeAnnotation } from './layoutSlice';
import { addHistoryAction } from '../history/historySlice';
import { selectElements } from '../selection/selectionSlice';
import { resetRouteEndpoints } from '../ui/uiSlice';
import { generateId } from '../../utils/geometry';
import { TRACK_3D_ELEVATION, DEFAULT_RAIL_HEIGHT_M } from '../../utils/layout3d';
import { LayoutElement, ElementType } from '../../types';

/** Generated track carries this tag so loading a new route replaces the prior one (not user track). */
export const LLM_ROUTE_TAG = 'llm-route';

/** Elevated rail types whose height must match the app's standard track profile. */
const RAIL_TYPES = new Set<ElementType>(['overhead-rail', 'overhead-curve', 'cross-rail']);

/**
 * The backend sometimes emits generated rails at a taller elevation / heightM than the app's
 * standard track. Force every generated rail onto the canonical height (TRACK_3D_ELEVATION /
 * DEFAULT_RAIL_HEIGHT_M) so generated routes sit at the same height as hand-placed track.
 */
export function normalizeGeneratedRouteHeights(elements: LayoutElement[]): LayoutElement[] {
  return elements.map((el) => {
    if (!RAIL_TYPES.has(el.type)) return el;
    return {
      ...el,
      position: { ...el.position, z: TRACK_3D_ELEVATION },
      customProperties: { ...el.customProperties, heightM: DEFAULT_RAIL_HEIGHT_M },
    };
  });
}

/** A start/end access point recorded in the route's plan. */
type RouteEndpoint = { role?: string; x?: number; y?: number };
type RouteLike = {
  elements?: Record<string, LayoutElement>;
  routePlan?: { endpoints?: RouteEndpoint[] };
};

/**
 * Tag the generated start/end nodes with the track-node role the app uses for gates: the START
 * point becomes an **Entry** node and the END point an **Exit** node (the "Type" field in the node
 * Properties panel). Entry/Exit are globally unique, so this replaces any previous ones.
 */
export function applyRouteEndpointRoles(dispatch: AppDispatch, route: RouteLike | null | undefined): void {
  const endpoints = route?.routePlan?.endpoints;
  if (Array.isArray(endpoints)) {
    for (const ep of endpoints) {
      if (!ep || typeof ep.x !== 'number' || typeof ep.y !== 'number') continue;
      const role = ep.role === 'start' ? 'entry' : ep.role === 'end' ? 'exit' : null;
      if (!role) continue;
      const label = ep.role === 'start' ? 'Start' : 'End';
      dispatch(
        updateTrackNodeAnnotation({ x: ep.x, y: ep.y, defaultName: label, changes: { role, name: label } })
      );
    }
  }
  // The capture overlay has served its purpose — the real start/end nodes are now part of the
  // route. Always clear the transient click-markers (even when the backend omits routePlan
  // endpoints) so the old START/END dots don't linger and duplicate the route's own nodes.
  dispatch(resetRouteEndpoints());
}

/** True for any element that belongs to a generated LLM route (track or start/end node). */
export function isGeneratedRouteElement(e: LayoutElement): boolean {
  return (
    e.customProperties?.generatedBy === LLM_ROUTE_TAG ||
    e.customProperties?.endpointRole === 'start' ||
    e.customProperties?.endpointRole === 'end'
  );
}

/**
 * Remove the previously generated LLM route (its track AND its start/end node markers) and clear the
 * transient capture dots. Call this when the user starts a NEW route so the old START/END points
 * don't linger on the canvas. Removal is one undo step; endpoint nodes are caught even if the
 * backend didn't tag them with `generatedBy`.
 */
export const clearGeneratedRouteThunk =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    dispatch(resetRouteEndpoints());
    const previous = Object.values(getState().layout.elements).filter(isGeneratedRouteElement);
    if (previous.length === 0) return;
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'remove',
        timestamp: Date.now(),
        elementIds: previous.map((e) => e.id),
        previousState: previous as Partial<LayoutElement>[],
      })
    );
    dispatch({
      type: removeElements.type,
      payload: previous.map((e) => e.id),
      meta: { skipHistory: true },
    });
  };

/**
 * Load a backend-generated route's `elements` onto the canvas, replacing any previously generated
 * (llm-route-tagged) track in a single undo step. Shared by the LLM-route button and the chat
 * assistant (both "generate" and "connect" return a full route to render). Returns the number of
 * track elements loaded.
 */
export const loadGeneratedRouteThunk =
  (route: RouteLike | null | undefined) =>
  (dispatch: AppDispatch, getState: () => RootState): number => {
    const newElements = normalizeGeneratedRouteHeights(
      Object.values(route?.elements ?? {}) as LayoutElement[]
    );
    if (newElements.length === 0) return 0;

    const state = getState();
    const previous = Object.values(state.layout.elements).filter(isGeneratedRouteElement);
    if (previous.length > 0) {
      dispatch({
        type: removeElements.type,
        payload: previous.map((e) => e.id),
        meta: { skipHistory: true },
      });
    }
    dispatch({ type: addElements.type, payload: newElements, meta: { skipHistory: true } });
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'replace',
        timestamp: Date.now(),
        elementIds: newElements.map((e) => e.id),
        previousState: previous as Partial<LayoutElement>[],
        newState: newElements as Partial<LayoutElement>[],
      })
    );
    dispatch(selectElements(newElements.map((e) => e.id)));
    applyRouteEndpointRoles(dispatch, route);   // start -> Entry, end -> Exit
    return newElements.length;
  };
